import React, { useCallback, useContext, useMemo, useState } from "react";
import { SearchInput } from "../../common/forms/SearchInput/SearchInput";
import { LucideIcon } from "../../common/icon/LucideIcon";
import TableView, { TableViewLabel } from "../../common/panels/TableView/TableView";
import { ColumnsSettings } from "../../common/tables/ColumnsSelector/ColumnsSelector";
import { AlertModal } from "../../common/modals/AlertModal/AlertModal";
import { EntityTable } from "../../common/tables/EntityTable/EntityTable";
import {
  GenericVirtualizedTableCells,
  SortState,
} from "../../common/tables/GenericVirtualizedTable/GenericVirtualizedTableTypes";
import { EntityFilterIndicator } from "../../common/tables/EntityFilterIndicator/EntityFilterIndicator";
import { useTabStore } from "../../common/tables/Tabs/useTabStore";
import { TableTabsDict } from "../../common/tables/Tabs/TableTabsTypes";
import { FileExportJob, FileExportJobFilters, FileExportJobFiltersTranslator } from "../../api/FileExportJobs";
import {
  genericDefaultSortState,
  getAsyncSortingFn,
  getSortStateArray,
  UseEntityTableColumns,
  UseEntityTableDefaultProps,
  UseEntityTableProps,
  useGenericVirtualizedTable,
  useGenericVirtualizedTableTabs,
} from "../../common/tables/GenericVirtualizedTable/useGenericVirtualizedTable";
import { EntityTableProps } from "../../common/entity/EntityInterfaces";
import { EntityTableDeleteButton } from "../../common/entity/entityComponents/EntityTableDeleteButton";
import { CircularProgress } from "../../common/loaders/CircularProgress/CircularProgress";
import { NotAvailable, NotSet } from "../../common/misc/UIconstants";
import { SessionContext } from "../../common/contexts/SessionContext";
import { DateTimeRenderer } from "../../common/datetime/DateTimeFormatter";
import { jobStateToIcon, jobStateToLabel } from "../../ViewerLayout/ViewerLayoutTypes";
import { FileExportDownloadButton } from "../FileExportJobsDetail";
import { EntityOrderBy, InferSortByOptions } from "../../api/GenericTypes";
import { useListen, LiveConnectionCallbacks } from "../../common/signalr/SignalRContext";

export const switchFileExportJobsDefaultSortState = (
  sortState: EntityOrderBy<InferSortByOptions<FileExportJobFilters>>[] | null | undefined
): SortState<InferSortByOptions<FileExportJobFilters>>[] => {
  const state = genericDefaultSortState(sortState);
  if (state) return state;

  return getSortStateArray(sortState, (sort) => {
    switch (sort?.property) {
      case "CREATED_AT":
        return {
          sortDirection: sort.sortDirection,
          headerId: "default-created-at",
          property: sort.property,
        };

      case "EXPIRES_AT":
        return {
          sortDirection: sort.sortDirection,
          headerId: "default-expires-at",
          property: sort.property,
        };

      default:
        return {
          sortDirection: "ASC",
          headerId: "default-id",
          property: "ID",
        };
    }
  });
};

export const defaultFileExportJobsFilters: FileExportJobFilters = {
  sortBy: [
    {
      property: "ID",
      sortDirection: "ASC",
    },
  ],
};

export const useFileExportJobsTableDefaults = ({ fieldLabels }: UseEntityTableDefaultProps<"fileExportJobs">) => {
  const defaults: ColumnsSettings<FileExportJob> = useMemo(
    () => ({
      "default-id": { pos: 0, active: false, header: fieldLabels.id, property: "id" },
      "default-name": { pos: 1, active: true, header: fieldLabels.name, property: "name" },
      "default-status": { pos: 2, active: true, header: fieldLabels.status, property: "status" },
      "default-progress": { pos: 3, active: true, header: fieldLabels.progress, property: "progress" },
      "default-is-downloaded": { pos: 4, active: true, header: fieldLabels.isDownloaded, property: "isDownloaded" },
      "default-number-of-items": { pos: 5, active: true, header: fieldLabels.numberOfItems, property: "numberOfItems" },
      "default-created-at": { pos: 6, active: true, header: fieldLabels.createdAt, property: "createdAt" },
      "default-expires-at": { pos: 7, active: true, header: fieldLabels.expiresAt, property: "expiresAt" },
      "default-media-type": { pos: 8, active: true, header: fieldLabels.mediaType, property: "mediaType" },
    }),
    [fieldLabels]
  );
  const tabStoreDefaults: TableTabsDict<FileExportJob, FileExportJobFilters, any> = useMemo(
    () => ({
      default: {
        tabId: "default",
        type: "fixed",
        label: "All",
        title: "All",
        icon: "house",
        align: "left",
        xPos: 0,
        settings: {
          columnSettings: {},
          columnWidths: {},
          filters: defaultFileExportJobsFilters,
          sidebarFilters: {},
        },
        forcedSettings: {
          columnSettings: {},
          columnWidths: {},
          filters: {},
          sidebarFilters: {},
        },
      },
    }),
    []
  );

  return { defaults, tabStoreDefaults };
};

export const useFileExportJobsTableColumns: UseEntityTableColumns<"fileExportJobs"> = ({
  entityConstants,
  fieldLabels,
  sort,
  setSort,
}: UseEntityTableProps<"fileExportJobs">) => {
  const columns = React.useMemo(() => {
    let headers: GenericVirtualizedTableCells<FileExportJob> = [
      {
        id: "default-id",
        Header: fieldLabels.id,
        accessor: (row) => (
          <span style={{ color: "var(--gray-400)" }}>
            <samp>{row.id}</samp>
          </span>
        ),
        width: 120,
        minWidth: 120,
        align: "left",
        ...getAsyncSortingFn("ID", sort, setSort),
      },
      {
        Header: fieldLabels.name,
        id: "default-name",
        accessor: (row) =>
          !!row.name ? (
            <div
              style={{ display: "flex", flexFlow: "row nowrap", gap: "5px", overflow: "hidden", alignItems: "center" }}
            >
              <div style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                <span style={{ fontWeight: 500, whiteSpace: "nowrap", marginRight: "5px" }}>
                  <LucideIcon name={entityConstants.icon} color={"var(--primary)"} /> {row.name}
                </span>
              </div>
            </div>
          ) : (
            NotSet
          ),
        minWidth: 150,
        width: 300,
        align: "left",
        ...getAsyncSortingFn("NAME", sort, setSort),
      },
      {
        Header: fieldLabels.status,
        id: "default-status",
        accessor: (row) => (
          <div className="flex row-nowrap align-center gap-5">
            <LucideIcon name={jobStateToIcon(row.status)} />
            {jobStateToLabel(row.status)}
          </div>
        ),
        minWidth: 150,
        width: 150,
        align: "left",
      },
      {
        Header: fieldLabels.progress,
        id: "default-progress",
        accessor: (row) => (
          <div className="flex row-nowrap align-center gap-5">
            <CircularProgress progress={row.progress ?? 0} width={24} style={{ stroke: "var(--primary)" }} />
            <span style={{ color: "var(--gray-600)" }}>{row.progress ?? 0}%</span>
          </div>
        ),
        minWidth: 150,
        width: 150,
        align: "left",
      },
      {
        Header: fieldLabels.isDownloaded,
        id: "default-is-downloaded",
        accessor: (row) => (
          <div className="flex row-nowrap align-center gap-5">
            <LucideIcon
              name={row.isDownloaded ? "circle-check" : "circle-x"}
              color={row.isDownloaded ? "var(--success)" : undefined}
            />
            <span>{row.isDownloaded ? "Yes" : "No"}</span>
          </div>
        ),
        minWidth: 150,
        width: 150,
        align: "left",
      },
      {
        Header: fieldLabels.numberOfItems,
        id: "default-number-of-items",
        accessor: (row) => <span>{row.numberOfItems ?? NotAvailable}</span>,
        minWidth: 150,
        width: 150,
        align: "left",
      },
      {
        Header: fieldLabels.createdAt,
        id: "default-created-at",
        accessor: (row) =>
          !!row.createdAt ? (
            <DateTimeRenderer date={row.createdAt} includeElapsed={true} includeDate includeTime />
          ) : (
            NotSet
          ),
        minWidth: 150,
        width: 300,
        align: "left",
        ...getAsyncSortingFn("CREATED_AT", sort, setSort),
      },
      {
        Header: fieldLabels.expiresAt,
        id: "default-expires-at",
        accessor: (row) =>
          !!row.expiresAt ? (
            <DateTimeRenderer date={row.expiresAt} includeElapsed={true} includeDate includeTime />
          ) : (
            NotSet
          ),
        minWidth: 150,
        width: 300,
        align: "left",
        ...getAsyncSortingFn("EXPIRES_AT", sort, setSort),
      },
      {
        Header: fieldLabels.mediaType,
        id: "default-media-type",
        accessor: (row) => <span>{row.isSingleFile ? row.mediaType ?? NotAvailable : "application/zip"}</span>,
        minWidth: 150,
        width: 150,
        align: "left",
      },
    ];

    return headers;
  }, [
    entityConstants.icon,
    fieldLabels.createdAt,
    fieldLabels.expiresAt,
    fieldLabels.id,
    fieldLabels.isDownloaded,
    fieldLabels.mediaType,
    fieldLabels.name,
    fieldLabels.numberOfItems,
    fieldLabels.progress,
    fieldLabels.status,
    setSort,
    sort,
  ]);
  return { columns };
};
export const FileExportJobsTable = ({
  entityApi,
  entityConstants,
  fieldLabels,
  permissions,
  routes,
}: EntityTableProps<"fileExportJobs">) => {
  const [showModal, setShowModal] = useState(false);
  const { bulkDeleteMutationAsync, isLoadingBulkDeleteMutation } = entityApi;

  const { defaults, tabStoreDefaults } = useFileExportJobsTableDefaults({ fieldLabels });

  const { selection, resultsCount, selectionPermissions, onCountChange, onSelectionChange, onSelectionPermissions } =
    useGenericVirtualizedTable<FileExportJob>();

  const { filters, forcedFilters, columnSetting, columnWidths, dispatchTabStore, currentTab, tabsLoading } =
    useTabStore<FileExportJob, FileExportJobFilters, any>({
      resource: entityConstants.resource,
      defaults: tabStoreDefaults,
    });

  const { functionRef, sort, setSort, searchValue, setSearchValue } = useGenericVirtualizedTableTabs({
    currentTab,
    tabsLoading,
    filters,
    switchSortState: switchFileExportJobsDefaultSortState,
    dispatchTabStore,
  });

  const { columns } = useFileExportJobsTableColumns({ entityConstants, fieldLabels, sort, setSort });

  const memoizedFilters = useMemo(() => ({ ...filters, ...forcedFilters }), [filters, forcedFilters]);

  const [refetchTimeout, setRefetchTimeout] = useState<NodeJS.Timeout | undefined>(undefined);
  const refetchJobs = useCallback(() => {
    if (refetchTimeout) clearTimeout(refetchTimeout);
    setRefetchTimeout(
      setTimeout(() => {
        functionRef.current?.refetch();
      }, 200)
    );
    return () => refetchTimeout && clearTimeout(refetchTimeout);
  }, [functionRef, refetchTimeout]);

  const { session } = useContext(SessionContext);

  const callback: LiveConnectionCallbacks["FileExportJobProgressChanged"] = useCallback(
    (jobId, userId, progress) => {
      console.log("FileExportJobProgressChanged", jobId, userId, progress);
      !!session?.userId && session?.userId === userId && refetchJobs();
    },
    [refetchJobs, session?.userId]
  );

  useListen("FileExportJobProgressChanged", callback);

  return (
    <TableView>
      <TableView.Head>
        <TableView.Head.Label>
          <TableViewLabel entityConstants={entityConstants} resultsCount={resultsCount} />
        </TableView.Head.Label>
      </TableView.Head>
      <TableView.Body>
        <TableView.Body.Content>
          <EntityTable>
            <EntityTable.Controls
              style={{
                borderTop: "0px",
                borderRadius: "0px",
              }}
            >
              <EntityFilterIndicator<FileExportJob, FileExportJobFilters>
                filters={filters}
                excludeFilters={{}}
                translatorConsts={FileExportJobFiltersTranslator}
              />
              <SearchInput searchValue={searchValue} setSearchValue={setSearchValue} placeholder="Search" />
              <FileExportDownloadButton id={selection.size === 1 ? Array.from(selection)[0] : undefined} />
              <EntityTableDeleteButton
                entityConstants={entityConstants}
                permissions={permissions}
                selection={selection}
                selectionPermissions={selectionPermissions}
                onClick={() => setShowModal(true)}
                className="btn btn-danger"
                label={selection.size > 1 ? `Delete (${selection.size})` : "Delete"}
              />
              <AlertModal
                type="danger"
                showModal={showModal}
                setShowModal={setShowModal}
                title={`${
                  selection.size === 0
                    ? "Select FileExportJob to delete"
                    : selection.size === 1
                    ? `Delete the selected ${entityConstants.entitySingular}?`
                    : `Delete the selected ${entityConstants.entityPlural}?`
                }`}
                description={`${`Proceeding will permanently delete the selected ${entityConstants.entityPlural}.`}`}
                proceedLabel="Delete"
                onProceed={async () => {
                  await bulkDeleteMutationAsync({
                    ids: Array.from(selection),
                    goBackOnSuccess: false,
                    showToast: false,
                    entityName: entityConstants.resource,
                    params: { deletePermanently: true },
                  }).catch(() => {});
                  functionRef.current?.resetSelection();
                  setShowModal(false);
                }}
                loading={isLoadingBulkDeleteMutation}
              />
            </EntityTable.Controls>
            <EntityTable.Body<FileExportJob, FileExportJobFilters>
              functionRef={functionRef}
              entityConstants={entityConstants}
              filters={memoizedFilters}
              columns={columns}
              columnSelect
              columnSetting={columnSetting}
              columnWidths={columnWidths}
              defaultColumnSettings={defaults}
              dispatchTabStore={dispatchTabStore}
              setResultsCount={onCountChange}
              onSelectionChange={onSelectionChange}
              onSelectionPermissions={onSelectionPermissions}
              showPermissionColumn
              loading={tabsLoading}
            />
          </EntityTable>
        </TableView.Body.Content>
      </TableView.Body>
    </TableView>
  );
};
